'use client'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { isColorLight } from '@/helpers/color-utils'
import { getDriverDisplayIdentifierDisplay } from '@/lib/driver-display-identifiers'
import { CarClassBadge } from './car-class-badge'
import ResultTimingCell from './result-timing-cell'

export default function OverallResultsTable({
  display,
  selectedCarClassId = 'all',
  driverDisplayIdentifier,
  driverDisplaySource,
  selectedUserId,
  showPoints = true,
  showSplits = true,
  showClassBadges = true,
  scope = 'Overall',
  tableLabel = 'Overall results',
}) {
  if (!display.available)
    return (
      <p role="status" className="py-6 text-neutral-blue-300">
        {display.reason}
      </p>
    )
  const rows = display.rows.filter(
    (row) =>
      selectedCarClassId === 'all' ||
      (selectedCarClassId === 'unassigned'
        ? !row.carClassId
        : row.carClassId === selectedCarClassId),
  )
  const showClasses =
    showClassBadges && display.rows.some((row) => row.result.carClass)
  const showTeams = display.rows.some((row) => row.result.team)
  return (
    <div className="flex flex-col gap-3">
      {display.notice && (
        <p role="status" className="text-sm text-neutral-blue-300">
          {display.notice}
        </p>
      )}
      <Table aria-label={tableLabel}>
        <TableHeader>
          <TableRow isHeader>
            <TableHead>
              <span className="md:hidden">Pos.</span>
              <span className="hidden md:inline">{scope} Pos.</span>
            </TableHead>
            <TableHead>Driver</TableHead>
            {showSplits && (
              <TableHead className="hidden md:table-cell">
                Tier / Split
              </TableHead>
            )}
            {showClasses && (
              <TableHead className="hidden md:table-cell">Car Class</TableHead>
            )}
            {showTeams && (
              <TableHead className="hidden md:table-cell">Team</TableHead>
            )}
            <TableHead>
              <span className="md:hidden">Gap</span>
              <span className="hidden md:inline">
                Gap to {scope.toLowerCase()} leader
              </span>
            </TableHead>
            {showPoints && <TableHead>Pts</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const result = row.result
            return (
              <TableRow
                key={row.id}
                className={`standings-user-${result.user?.id}`}
                aria-selected={
                  selectedUserId
                    ? result.user?.id === selectedUserId
                    : undefined
                }
              >
                <TableCell>{row.position}</TableCell>
                <TableCell className="min-w-28 md:min-w-40">
                  {getDriverDisplayIdentifierDisplay(
                    result,
                    driverDisplayIdentifier,
                    { source: driverDisplaySource },
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-1 md:hidden">
                    {showSplits && <SplitBadge split={result.split} />}
                    {showClasses && (
                      <CarClassBadge carClass={result.carClass} />
                    )}
                    {showTeams && result.team && (
                      <span className="w-full text-xs text-neutral-blue-300">
                        {result.team.name}
                      </span>
                    )}
                  </div>
                </TableCell>
                {showSplits && (
                  <TableCell className="hidden md:table-cell">
                    <SplitBadge split={result.split} />
                  </TableCell>
                )}
                {showClasses && (
                  <TableCell className="hidden md:table-cell">
                    <CarClassBadge carClass={result.carClass} />
                  </TableCell>
                )}
                {showTeams && (
                  <TableCell className="hidden md:table-cell">
                    {result.team?.name || '—'}
                  </TableCell>
                )}
                <TableCell>
                  <ResultTimingCell
                    position={row.position}
                    status={row.status}
                    {...row.timingOverride}
                    penaltyTime={result.penaltyTime}
                  />
                </TableCell>
                {showPoints && <TableCell>{result.total ?? 0}</TableCell>}
              </TableRow>
            )
          })}
          {rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={
                  3 +
                  Number(showSplits) +
                  Number(showClasses) +
                  Number(showTeams) +
                  Number(showPoints)
                }
              >
                {display.rows.length === 0
                  ? 'No results for this session.'
                  : 'No results for this car class.'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

function SplitBadge({ split }) {
  if (!split) return <span className="text-xs">Unassigned</span>
  return (
    <span
      className="whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold"
      style={{
        backgroundColor: split.color ?? '#ffffff',
        color: isColorLight(split.color ?? '#ffffff') ? '#000000' : '#ffffff',
      }}
    >
      {split.name}
    </span>
  )
}
